import { useMemo } from "react";
import { useTran } from "@/hooks";
import { Check, X, Pencil, Trash2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import { DataTable } from "@/components/common";
import { buildTableDefaults } from "@/components/common/data-table-helpers";
import {
  ADMIN_ROLES,
  buildPermissionRows,
  formatRoleLabel,
  MODULE_TABLE_HEADERS,
  normalizeId,
} from "../utils/roles-permissions";
import { cn } from "@/lib/utils/index";

const renderPermissionFlag = (value) => (
  <span className="inline-flex items-center justify-center rounded px-2 py-0.5 text-[11px] font-medium">
    {value ? (
      <Check className="h-3.5 w-3.5 text-emerald-600" />
    ) : (
      <X className="h-3.5 w-3.5 text-red-600" />
    )}
  </span>
);

const RolesPermissionsTable = ({
  data = [],
  loading = false,
  isAdminGroup = false,
  companyId = "",
  roleUsageById = {},
  roleUsageLoading = false,
  onEdit,
  onDelete,
}) => {
  const { t } = useTran();
  const { emptyMessage, loadingMessage, getRowProps } = buildTableDefaults({
    t,
    emptyMessageKey: "No roles available.",
  });
const userStr = JSON.parse(localStorage.getItem("user"));

  const columns = useMemo(
    () => [
      {
        key: "index",
        header: t("No."),
        cellClassName: "align-top",
        render: (item,index) => (
          <span className="font-medium text-foreground">
            {index + 1}
          </span>
        ),
      },
      {
        key: "role",
        header: t("Role Name"),
        cellClassName: "align-top",
        render: (item) => (
          <span className="font-medium text-foreground">
            {ADMIN_ROLES.includes(userStr.role)?formatRoleLabel(item.role) : item.role}
          </span>
        ),
      },
      {
        key: "permissions",
        header: t("Permissions"),
        cellClassName: "align-top",
        render: (item) => {
          const permissionRows = buildPermissionRows(item.permissions);
          return (
            <div className="w-full overflow-auto">
              {permissionRows.length > 0 ? (
                <div className="min-w-[420px] overflow-hidden rounded-radius border border-border">
                  <div className="grid grid-cols-6 bg-muted/40 px-3 py-2 text-[14px] font-semibold tracking-wide text-muted-foreground text-primary">
                    {MODULE_TABLE_HEADERS.map((header) => (
                      <span
                        key={header}
                        className={cn(
                          "font-medium",
                          header === "Module" ? "col-span-2" : "text-center",
                        )}
                      >
                        {t(header)}
                      </span>
                    ))}
                  </div>
                  {permissionRows.map((row) => (
                    <div
                      key={`${item.role}-${row.label}`}
                      className="grid grid-cols-6 items-center gap-2 border-t border-border px-2 py-1 text-xs"
                    >
                      <span className="col-span-2 text-foreground">
                        {t(row.label)}
                      </span>
                      <span className="text-center">
                        {renderPermissionFlag(row.actions.create)}
                      </span>
                      <span className="text-center">
                        {renderPermissionFlag(row.actions.update)}
                      </span>
                      <span className="text-center">
                        {renderPermissionFlag(row.actions.view)}
                      </span>
                      <span className="text-center">
                        {renderPermissionFlag(row.actions.delete)}
                      </span>
                    </div>
                  ))}
                </div>
              ) : (
                <span className="text-xs text-muted-foreground">
                  {t("No permissions assigned")}
                </span>
              )}
            </div>
          );
        },
      },
      {
        key: "actions",
        header: t("Actions"),
        align: "right",
        hidden: isAdminGroup,
        cellClassName: "align-top",
        render: (item) => (
          <div className="flex justify-end gap-2">
            {(() => {
              const roleId = normalizeId(
                item?.raw?._id ||
                  item?.raw?.id ||
                  item?.raw?.key ||
                  item?.raw?.role,
              );
              const isCompanyRole =
                normalizeId(item?.raw?.companyId) === companyId;
              const isRoleUsed = Boolean(roleId && roleUsageById[roleId]);
              const disableDelete =
                roleUsageLoading || !isCompanyRole || isRoleUsed;
              const deleteTitle = isRoleUsed
                ? t("Role is assigned to users.")
                : roleUsageLoading
                  ? t("Checking role usage...")
                  : undefined;

              return (
                <>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    className="gap-1"
                    icon = {Pencil}
                    onClick={() => onEdit?.(item)}
                  >
                    {t("Edit")}
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="destructive"
                    className="gap-1"
                    onClick={() => onDelete?.(item)}
                    disabled={disableDelete}
                    title={deleteTitle}
                    icon={Trash2}
                  >
                    {t("Delete")}
                  </Button>
                </>
              );
            })()}
          </div>
        ),
      },
    ],
    [
      t,
      isAdminGroup,
      companyId,
      roleUsageById,
      roleUsageLoading,
      onEdit,
      onDelete,
    ],
  );

  return (
    <DataTable
      columns={columns}
      data={data}
      loading={loading}
      emptyMessage={emptyMessage}
      loadingMessage={loadingMessage}
      getRowKey={(row) => row.id || row.role}
      getRowProps={getRowProps}
    />
  );
};

export default RolesPermissionsTable;
